
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  createSubscription, 
  updateSubscription 
} from '@/services/subscription';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';

export function useSubscriptionForm(subscriptionId?: string) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isEditMode = !!subscriptionId;

  const handleSubmit = useCallback(async (formData: any) => {
    console.log("useSubscriptionForm - Starting submission with data:", formData);
    console.log("PROM Avarn value:", formData.prom_avarn);
    console.log("Invoice reference value:", formData.invoice_reference);
    
    if (!user) {
      console.error("User is not authenticated");
      toast.error("You must be logged in to create or update a subscription");
      return false;
    }
    
    if (!formData.customer_id) {
      console.error("Missing customer_id in form data");
      toast.error("Customer ID is required");
      return false;
    }
    
    // Only validate services array if this is not edit mode and if services are provided
    // When updating from the edit page, services are managed separately
    if (!isEditMode && formData.services && !formData.services.length) {
      console.error("No services selected");
      toast.error("At least one service must be selected");
      return false;
    }
    
    setIsSubmitting(true);
    
    try {
      // Add created_by for new subscriptions
      if (!isEditMode) {
        formData.created_by = user.email || 'system';
      }
      
      // Ensure prom_avarn is included in the submission
      formData.prom_avarn = formData.prom_avarn || null;
      formData.invoice_reference = formData.invoice_reference || null;
      
      // We expect services to be already filtered by this point if they exist
      if (formData.services) {
        console.log("Proceeding with subscription creation/update, services:", formData.services);
      } else {
        console.log("Proceeding with subscription update without changing services");
      }
      
      let result;
      if (isEditMode && subscriptionId) {
        // When updating from edit page, don't touch the services array
        if (formData.services) {
          delete formData.services;
        }
        
        // Explicitly remove updated_by field if it exists in formData
        if ('updated_by' in formData) {
          delete formData.updated_by;
        }
        
        // Create a clean payload for update
        const updatePayload = { ...formData };
        
        console.log("Updating subscription with clean payload:", updatePayload);
        result = await updateSubscription(subscriptionId, updatePayload);
      } else {
        console.log("Creating subscription with data:", formData);
        result = await createSubscription({
          ...formData,
          created_by: user.email || 'system'
        });
        console.log("Subscription created:", result);
      }
      
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (isEditMode && subscriptionId) {
        queryClient.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
      }
      
      return true;
    } catch (error) {
      console.error('Error saving subscription:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('violates row-level security policy')) {
          toast.error('Permission denied: You do not have permission to update this subscription');
        } else {
          toast.error(isEditMode 
            ? `Failed to update subscription: ${error.message}` 
            : `Failed to create subscription: ${error.message}`
          );
        }
      } else {
        toast.error(isEditMode 
          ? 'Failed to update subscription. Please try again.' 
          : 'Failed to create subscription. Please try again.'
        );
      }
      
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [isEditMode, subscriptionId, user, queryClient]);

  return {
    handleSubmit,
    isSubmitting,
    isEditMode
  };
}
